import React, { useEffect, useState } from "react";
import { api } from "./lib/api";
import { formatKSADateTime } from "./lib/ksaTime";

// Admin-only widget that surfaces the latest RBAC regression audit
// (run weekly by the backend scheduler) and lets admins trigger a fresh
// run on demand. Helps detect hardcoded role gates before users hit them.
export default function RbacAuditPanel() {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadLast = async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/rbac-audit/last");
      setReport(data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || "تعذّر تحميل آخر تدقيق.");
    }
  };

  const runNow = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/rbac-audit/run");
      setReport(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "تعذّر تشغيل التدقيق. حاول مرة أخرى.");
    }
    setBusy(false);
  };

  useEffect(() => { loadLast(); }, []);

  const checks = report?.checks || [];
  const total = report?.total || 0;
  const failed = report?.failed || 0;
  const passed = report?.passed || 0;
  const isOk = total > 0 && failed === 0;

  return (
    <div className="card" data-testid="rbac-audit-panel"
      style={{ padding: 16, background: "#fff", border: "1px solid var(--border)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0, color: "var(--brown-deep)", fontSize: 16 }}>
            🛡 تدقيق RBAC الأسبوعي
          </h3>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4, lineHeight: 1.7 }}>
            يتحقق أن كل صفحة محمية تحترم نظام الصلاحيات الديناميكي — يكتشف أي gate يفحص اسم الدور حرفياً ويتجاوز RBAC.
          </div>
        </div>
        <button type="button" onClick={runNow} disabled={busy}
          data-testid="rbac-audit-run-btn"
          style={{
            padding: "8px 14px", background: busy ? "#cbd5e1" : "var(--brown-deep)",
            color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 800,
            cursor: busy ? "wait" : "pointer", whiteSpace: "nowrap",
          }}>
          {busy ? "⏳ جاري التشغيل..." : "▶️ شغّل التدقيق الآن"}
        </button>
      </div>

      {error && (
        <div style={{ marginTop: 10, padding: 8, background: "rgba(220,38,38,.08)", color: "var(--danger)", borderRadius: 6, fontSize: 12.5 }}>
          ⚠️ {error}
        </div>
      )}

      {report && total > 0 && (
        <>
          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 120px", padding: 10, background: isOk ? "rgba(16,185,129,.10)" : "rgba(220,38,38,.10)", borderRadius: 6 }}>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>الحالة</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: isOk ? "var(--success)" : "var(--danger)" }}>
                {isOk ? "✅ سليم" : `❌ ${failed} فشل`}
              </div>
            </div>
            <div style={{ flex: "1 1 120px", padding: 10, background: "rgba(0,0,0,.04)", borderRadius: 6 }}>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>نجح</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text)" }}>{passed} / {total}</div>
            </div>
            <div style={{ flex: "1 1 120px", padding: 10, background: "rgba(0,0,0,.04)", borderRadius: 6 }}>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>آخر تشغيل</div>
              <div style={{ fontSize: 12, color: "var(--text)", marginTop: 4 }}>
                {report.finished_at ? formatKSADateTime(new Date(report.finished_at), { dateStyle: "short", timeStyle: "short" }) : "—"}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12, maxHeight: 320, overflow: "auto", border: "1px solid var(--border)", borderRadius: 6 }}>
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
              <thead style={{ position: "sticky", top: 0, background: "#f8fafc" }}>
                <tr>
                  <th style={{ textAlign: "start", padding: 8, borderBottom: "1px solid var(--border)" }}>الصفحة</th>
                  <th style={{ textAlign: "start", padding: 8, borderBottom: "1px solid var(--border)" }}>الصلاحية المطلوبة</th>
                  <th style={{ textAlign: "center", padding: 8, borderBottom: "1px solid var(--border)" }}>Admin</th>
                  <th style={{ textAlign: "center", padding: 8, borderBottom: "1px solid var(--border)" }}>دور مخصص</th>
                  <th style={{ textAlign: "center", padding: 8, borderBottom: "1px solid var(--border)" }}>النتيجة</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((c, i) => (
                  <tr key={i} data-testid={`rbac-audit-row-${c.permission}`} style={{ background: c.passed ? "transparent" : "rgba(220,38,38,.05)" }}>
                    <td style={{ padding: 8, borderBottom: "1px solid var(--border)" }}>
                      <div style={{ fontWeight: 700 }}>{c.label}</div>
                      <div style={{ color: "var(--muted)", fontSize: 11 }}>{c.method} {c.path}</div>
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid var(--border)", fontFamily: "monospace", fontSize: 11 }}>{c.permission}</td>
                    <td style={{ padding: 8, borderBottom: "1px solid var(--border)", textAlign: "center" }}>{c.admin_status ?? "—"}</td>
                    <td style={{ padding: 8, borderBottom: "1px solid var(--border)", textAlign: "center" }}>
                      <span style={{ color: c.custom_status && c.custom_status < 400 ? "var(--success)" : "var(--danger)", fontWeight: 700 }}>
                        {c.custom_status ?? "—"}
                      </span>
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid var(--border)", textAlign: "center" }}>
                      {c.passed ? "✅" : "❌"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!isOk && (
            <div style={{ marginTop: 10, padding: 10, background: "rgba(217,119,6,.10)", borderRadius: 6, fontSize: 12, color: "#92400E", lineHeight: 1.7 }}>
              ⚠️ هناك صفحات لا تحترم نظام الصلاحيات الديناميكي. راجع gates الـ Backend لهذه الصلاحيات — على الأرجح يفحصون اسم الدور حرفياً.
            </div>
          )}
        </>
      )}

      {(!report || total === 0) && !error && (
        <div style={{ marginTop: 12, padding: 12, background: "rgba(0,0,0,.03)", borderRadius: 6, fontSize: 12.5, color: "var(--muted)", lineHeight: 1.7 }}>
          لم يُشغَّل التدقيق بعد. يعمل تلقائياً كل يوم أحد 3:00 صباحاً، أو شغّله يدوياً من الزر أعلاه.
        </div>
      )}
    </div>
  );
}
